import React, {
  useEffect,
  useState,
  useReducer,
  useCallback,
  useRef,
} from "react";
import MiniSearch from "minisearch";
import useSWRImmutable from "swr/immutable";
import MapCumulativeChart from "../../../../../../components/races/national/CumulativeChart";
import MapChartLegend from "../../../../../../components/races/national/ChartLegend";
import { groupBy, max, min, flatten } from "lodash";
import {
  getPresidentList,
  getVicePresidentList,
  getRegions,
  getProvinces,
  getIndependentCities,
  getProvinceData,
} from "../../../../../api/ph-api";
import {
  getFormattedDateTime,
  getTransmissionPercentage,
} from "../../../../../../helpers/util";
import styles from "../../../../../../styles/races/embed/Graph.module.scss";

export async function getRegionData(regionLink) {
  try {
    const res = await fetch(regionLink, {
      method: "GET",
      headers: {
        Authorization: process.env.API_TOKEN,
        "x-bypass-ratelimit": "pzQ!5eP7CWnUQSc</ojHpsd/gqfUJKgfKw=dH93po?fK",
      },
    });
    return await res.json();
  } catch (err) {
    return null;
  }
}

export const getStaticProps = async () => {
  const presidential = await getPresidentList();
  const vPresidential = await getVicePresidentList();
  const regionList = await getRegions();
  const provincesList = await getProvinces();
  const cityList = await getIndependentCities();
  const philippines = {
    slug: "philippines",
    name: "PH",
    display_name: "Philippines",
  };

  // Remove NCR from Provinces List
  const provincesNoNCR = provincesList.results.filter(
    (province) => province.slug !== "ncr"
  );
  provincesList.results = [...provincesNoNCR];
  provincesList.count = provincesList.count - 1;

  // Add candidateColor
  presidential.results.forEach((candidate) => {
    candidate.selected = true;
    candidate.votes = "0";
    candidate.percentage = 0;
    switch (candidate.last_name.toLowerCase()) {
      case "robredo":
        candidate.color = "rgba(252, 106, 223, 1)";
        candidate.legend = "rect";
        break;
      case "marcos":
        candidate.color = "rgba(192, 36, 2, 1)";
        candidate.legend = "rectRot";
        break;
      case "pacquiao":
        candidate.color = "rgba(57, 198, 240, 1)";
        candidate.legend = "circle";
        break;
      case "domagoso":
        candidate.color = "rgba(64, 118, 255, 1)";
        candidate.legend = "rectRot";
        break;
      case "de guzman":
        candidate.color = "rgba(242, 106, 90, 1)";
        candidate.legend = "rect";
        break;
      case "lacson":
        candidate.color = "rgba(183, 125, 222, 1)";
        candidate.legend = "circle";
        break;
      case "mangondato":
        candidate.color = "rgba(72, 167, 104, 1)";
        candidate.legend = "rect";
        break;
      case "montemayor":
        candidate.color = "rgba(202, 185, 36, 1)";
        candidate.legend = "triangle";
        break;
      case "abella":
        candidate.color = "rgba(92, 161, 144, 1)";
        candidate.legend = "circle";
        break;
      case "gonzales":
        candidate.color = "rgba(197, 101, 12, 1)";
        candidate.legend = "triangle";
        break;
      default:
        candidate.color = "rgba(0, 0, 0, 1)";
        candidate.legend = "circle";
        break;
    }
  });

  vPresidential.results.forEach((candidate) => {
    candidate.selected = true;
    candidate.votes = "0";
    candidate.percentage = 0;
    switch (candidate.last_name.toLowerCase()) {
      case "pangilinan":
        candidate.color = "rgba(252, 106, 223, 1)";
        candidate.legend = "rect";
        break;
      case "duterte":
        candidate.color = "rgba(192, 36, 2, 1)";
        candidate.legend = "rectRot";
        break;
      case "atienza":
        candidate.color = "rgba(57, 198, 240, 1)";
        candidate.legend = "circle";
        break;
      case "ong":
        candidate.color = "rgba(64, 118, 255, 1)";
        candidate.legend = "rectRot";
        break;
      case "bello":
        candidate.color = "rgba(242, 106, 90, 1)";
        candidate.legend = "rect";
        break;
      case "sotto":
        candidate.color = "rgba(183, 125, 222, 1)";
        candidate.legend = "circle";
        break;
      case "serapio":
        candidate.color = "rgba(72, 167, 104, 1)";
        candidate.legend = "rect";
        break;
      case "david":
        candidate.color = "rgba(202, 185, 36, 1)";
        candidate.legend = "triangle";
        break;
      case "lopez":
        candidate.color = "rgba(92, 161, 144, 1)";
        candidate.legend = "rectRot";
        break;
      default:
        candidate.color = "rgba(0, 0, 0, 1)";
        candidate.legend = "circle";
        break;
    }
  });

  // THIS SHOULD BE REPLACED BY AN API CALL
  const getTrStatusByLocation = async (locationSlug) => {
    return {
      votes: 0,
      percentage: 0,
      timestamp: "May 9 2022 17:00:00 GMT+0800",
    };
  };

  await Promise.all(
    cityList.cities.map(async (location) => {
      const { votes, percentage, timestamp } = await getTrStatusByLocation(
        location.slug
      );
      location.votes = votes;
      location.percentage = percentage;
      location.timestamp = timestamp;

      let provinceSlug = location.province.slug;
      const provinceData = await getProvinceData(provinceSlug);
      const regionData = await getRegionData(provinceData.region);
      location.region = regionData;
    })
  );

  await Promise.all(
    provincesList.results.map(async (location) => {
      const { votes, percentage, timestamp } = await getTrStatusByLocation(
        location.slug
      );
      location.votes = votes;
      location.percentage = percentage;
      location.timestamp = timestamp;

      let provinceSlug = location.slug;
      const provinceData = await getProvinceData(provinceSlug);
      const regionData = await getRegionData(provinceData.region);
      location.region = regionData;
    })
  );

  await Promise.all(
    regionList.results.map(async (location) => {
      const { votes, percentage, timestamp } = await getTrStatusByLocation(
        location.slug
      );
      location.votes = votes;
      location.percentage = percentage;
      location.timestamp = timestamp;
    })
  );

  const { votes, percentage, timestamp } = await getTrStatusByLocation(
    philippines.slug
  );
  philippines.votes = votes;
  philippines.percentage = percentage;
  philippines.timestamp = timestamp;

  const slugList = [
    ...provincesList.results,
    ...regionList.results,
    ...cityList.cities,
  ].map((region) => region.slug);
  slugList.push("philippines");
  const splitSlugLocation = [];
  while (slugList.length > 0) {
    splitSlugLocation.push(slugList.splice(0, 10));
  }
  const slugPresLocationRequest = [];
  const slugVicePresLocationRequest = [];
  splitSlugLocation.forEach((value) => {
    const requestPresident = fetch(`${cfUrl}/snapshotResults`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        data: {
          locationSlugs: value.join(","),
          interval: 60000,
          raceType: "PRESIDENT PHILIPPINES",
        },
      }),
    }).then(async (res) => {
      const x = await res.json();
      return x;
    });
    slugPresLocationRequest.push(requestPresident);
  });
  const slugPresRequestResult = await Promise.all(slugPresLocationRequest);

  let presLocation = [];
  presLocation = flatten(slugPresRequestResult.map((data) => data?.result));

  if (
    !presidential ||
    !vPresidential ||
    !regionList ||
    !provincesList ||
    !cityList ||
    !presLocation
  ) {
    return {
      props: {
        presidential,
        vPresidential,
        regionList,
        provincesList,
        cityList,
        philippines,
        presLocation,
      },
      revalidate: 5,
    };
  }

  return {
    props: {
      presidential,
      vPresidential,
      regionList,
      provincesList,
      cityList,
      philippines,
      presLocation,
    },
  };
};

const reducer = (state, { type, slug, mutation }) => {
  if (type === "set") {
    return mutation;
  } else if (type === "update") {
    const candidateIndex = state.findIndex(
      (candidate) => candidate.wp_slug === slug
    );
    if (candidateIndex === -1) {
      return [...state];
    }

    state[candidateIndex] = {
      ...state[candidateIndex],
      ...mutation,
    };

    if (mutation.votes) {
      state.sort((a, b) => b.votes - a.votes);
    }

    return [...state];
  } else {
    return [...state];
  }
};

const cfUrl = process.env.API_CF_URL;
const EmbedCumulativeMapPresident = ({
  presidential,
  vPresidential,
  url,
  regionList,
  provincesList,
  cityList,
  philippines,
  presLocation,
}) => {
  const ELECTION_DAY_TRANSMISSION_START = 1652094000000;
  const TOTAL_PRECINCTS_PH = regionList?.results
    ? (regionList?.results || [])
        .map((region) => (region || {}).precincts_count)
        .reduce((a, b) => a + b, 0)
    : null;
  const TOTAL_PRECINCTS_OAV = (
    regionList?.results.find((region) => region.slug === "oav") || {}
  ).precincts_count;
  // const TIMELAPSE_START_DATE = new Date(
  //   "May 9 2022 17:00:00 GMT+0800"
  // ).getTime();
  // const TIMELAPSE_END_DATE = new Date(
  //   "May 11 2022 18:00:00 GMT+0800"
  // ).getTime();
  const TIMELAPSE_INTERVAL_CHART = 60 * 60 * 1000; // 1 hour
  const TIMELAPSE_INTERVAL_MAP = 15 * 60 * 1000;

  const miniSearch = new MiniSearch({
    fields: ["name", "display_name"],
    storeFields: ["name", "display_name", "slug", "province", "region"],
  });
  const [presidentialList, setPresidentialList] = useReducer(reducer, [
    ...presidential.results,
  ]);
  const [vPresidentialList, setVPresidentialList] = useReducer(reducer, [
    ...vPresidential.results,
  ]);
  const [candidateDataLoading, setCandidateDataLoading] = useState(true);
  const [candidateDataError, setCandidateDataError] = useState("");
  const [mapType, setMapType] = useState("votes");
  const [raceType, setRaceType] = useState("presidential");
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedRegionCopy, setSelectedRegionCopy] = useState(null);
  const [regionFilter, setRegionFilter] = useState("");
  const [regionFilterActive, setRegionFilterActive] = useState(false);
  const [selectedProvinceICity, setSelectedProvinceICity] = useState(null);
  const [selectedProvinceICityCopy, setSelectedProvinceICityCopy] =
    useState(null);
  const [provinceICityFilter, setProvinceICityFilter] = useState("");
  const [provinceICityFilterActive, setProvinceICityFilterActive] =
    useState(false);
  const [candidateFilter, setCandidateFilter] = useState(["all", "all"]);
  const [tooltipContent, setTooltipContent] = useState({
    title: "",
    description: "",
  });
  const [isMounted, setIsMounted] = useState(false);
  const [selectedMap, setSelectedMap] = useState("Philippines");
  const [showCandidateList, setShowCandidateList] = useState(false);
  const [currentMapState, setCurrentMapState] = useState([]);
  const [currentCandidateList, setCurrentCandidateList] = useState([]);
  const [lastTransmissionDate, setLastTransmissionDate] = useState(null);
  const [locationSlug, setLocationSlug] = useState("philippines");
  const [mapTimeRange, setMapTimeRange] = useState({
    minTimestamp: 0,
    maxTimestamp: 0,
    markerInterval: 0,
  });
  const [locationData, setLocationData] = useState({});
  const [selectedTimeStep, setSelectedTimeStep] = useState(0);
  const [timeSliderManuallyChanged, setTimeSliderManuallyChanged] =
    useState(false);
  const [locationsData, setLocationsData] = useState([...presLocation]);
  const [snapshotData, setSnapshotData] = useState([...presLocation]);
  const tooltipRef = useRef(null);
  const mouseCoords = { x: 0, y: 0 };
  const slugList = [
    ...provincesList.results,
    ...regionList.results,
    ...cityList.cities,
  ].map((region) => region.slug);
  const _raceType =
    raceType === "presidential"
      ? "PRESIDENT PHILIPPINES"
      : "VICE PRESIDENT PHILIPPINES";
  const fetcher = (url, body) =>
    fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body,
    }).then(async (res) => {
      return res.json();
    });

  const {
    data: rawCumulativeVotesChartData,
    error: cumulativeVotesChartError,
  } = useSWRImmutable(
    [
      `${cfUrl}/PHCumulativeVotes`,
      JSON.stringify({
        data: {
          raceType: _raceType,
        },
      }),
    ],
    fetcher,
    {
      refreshInterval: 0,
      shouldRetryOnError: true,
      revalidateOnReconnect: true,
      refreshWhenOffline: true,
      errorRetryInterval: 5000,
      errorRetryCount: 1,
    }
  );
  const cumulativeVotesChartData = rawCumulativeVotesChartData?.result;

  const {
    data: rawLatestLocationResultData,
    error: latestLocationResultError,
  } = useSWRImmutable(
    [
      `${cfUrl}/latestLocationResults`,
      JSON.stringify({
        data: {
          locationSlug,
          raceType: _raceType,
        },
      }),
    ],
    fetcher,
    {
      refreshInterval: 0,
      shouldRetryOnError: true,
      revalidateOnReconnect: true,
      refreshWhenOffline: true,
      errorRetryInterval: 5000,
      errorRetryCount: 1,
    }
  );
  const latestLocationResultData = rawLatestLocationResultData?.result;

  useEffect(() => {
    setIsMounted(true);
    const updateCandidateData = async () => {
      setLocationSlug(
        selectedMap === "OAV"
          ? "oav"
          : selectedProvinceICity
          ? selectedProvinceICity.slug
          : selectedRegion
          ? selectedRegion.slug
          : "philippines"
      );

      const _raceType =
        raceType === "presidential"
          ? "PRESIDENT PHILIPPINES"
          : "VICE PRESIDENT PHILIPPINES";
      try {
        let candidateData = currentCandidateList;

        if (candidateData && candidateData.length) {
          candidateData = candidateData
            .sort((a, b) => b.voteCount - a.voteCount)
            .map((items, i) => ({
              rank: i + 1,
              ...items,
            }));
        }

        candidateData.forEach((dataPoint) => {
          const mutation = {
            rank: dataPoint.rank,
            votes: dataPoint.voteCount,
            percentage: dataPoint.votePercentage,
          };

          const params = {
            type: "update",
            slug: dataPoint.candidateSlug,
            mutation,
          };

          if (raceType === "presidential") {
            setPresidentialList(params);
          } else {
            setVPresidentialList(params);
          }
        });
      } catch (err) {
        return err;
      }
    };

    setCandidateDataLoading(true);
    updateCandidateData()
      .then(() => {
        setCandidateDataError("");
        setCandidateDataLoading(false);
      })
      .catch((err) => {
        setCandidateDataError(err);
        setCandidateDataLoading(false);
      });
  }, [
    selectedRegion,
    selectedProvinceICity,
    raceType,
    selectedMap,
    latestLocationResultData,
    currentCandidateList,
  ]);

  useEffect(() => selectAllCandidates(true), []);

  useEffect(() => cumulativeVotesData(), []);

  /** When snapshotData changes, recalculate time lapse props */
  useEffect(() => {
    if (!locationsData?.length) {
      return;
    }

    const locDataByTs = groupBy(locationsData, "intervalTimestamp");
    setLocationData(locDataByTs);

    if (!Object.keys(locDataByTs).length) {
      return;
    }

    let markerInterval = 6 * 60 * 60 * 1000;
    const maxTimestamp = parseInt(max(Object.keys(locDataByTs)), 10);
    const minTimestamp = parseInt(min(Object.keys(locDataByTs)), 10);

    if (maxTimestamp - minTimestamp < 12 * 60 * 60 * 1000) {
      markerInterval = 2 * 60 * 60 * 1000;
    } else if (maxTimestamp - minTimestamp < 6 * 60 * 60 * 1000) {
      markerInterval = 60 * 60 * 1000;
    } else if (maxTimestamp - minTimestamp < 60 * 60 * 1000) {
      markerInterval = 15 * 60 * 1000;
    } else {
      markerInterval = maxTimestamp - minTimestamp;
    }

    setMapTimeRange({ minTimestamp, maxTimestamp, markerInterval });
  }, [locationsData, raceType]);

  useEffect(() => {
    if (!timeSliderManuallyChanged) {
      setSelectedTimeStep(mapTimeRange.maxTimestamp);
    }
  }, [mapTimeRange.maxTimestamp, timeSliderManuallyChanged]);

  useEffect(() => {
    if (showCandidateList) {
      const rankingContainer = document.querySelector(
        "[data-id='map-ranking-container'"
      );
      if (!rankingContainer) {
        return;
      }
      rankingContainer.scrollTo({
        behavior: "instant",
        top: 0,
      });
    }
  }, [showCandidateList]);
  if (candidateDataError)
    return <p>Something went wrong, Please try again later!</p>;
  /****************************************************************************/
  /* CANDIDATES FUNCTIONS
  /****************************************************************************/
  /* Candidate Filter (Select All | Clear Selection) - can either set all candidates selected state to "true" or "false" */
  const selectAllCandidates = (selected) => {
    let candidatesArray = [...presidentialList];
    let candidateIndex = 0;

    if (raceType === "vice-presidential") {
      candidatesArray = [...vPresidentialList];
      candidateIndex = 1;
    }

    candidatesArray.forEach((candidate) => (candidate.selected = selected));

    if (raceType === "presidential") {
      setPresidentialList({ type: "set", mutation: candidatesArray });
    } else {
      setVPresidentialList({ type: "set", mutation: candidatesArray });
    }

    if (selected) {
      const filterArray = [...candidateFilter];
      filterArray[candidateIndex] = "all";
      setCandidateFilter(filterArray);
    } else {
      const filterArray = [...candidateFilter];
      filterArray[candidateIndex] = "clear";
      setCandidateFilter(filterArray);
    }
  };
  /****************************************************************************/
  /* CHART FUNCTIONS
  /****************************************************************************/
  const cumulativeVotesData = () => {
    const labels = [];
    const datasets = [];

    if (cumulativeVotesChartData && cumulativeVotesChartData.length) {
      let candidateList = [...presidentialList];
      candidateList.forEach((candidate) => {
        if (candidate.selected) {
          const data = [];
          const timestamps = [];
          const transmissionPercentage = [];
          // push 0 state
          // for testing purposes, added handling to use min timestamp minus 15 minutes if not yet actual transmission
          data.push({
            x:
              cumulativeVotesChartData[0].timestamp <
              ELECTION_DAY_TRANSMISSION_START
                ? new Date(
                    cumulativeVotesChartData[0].timestamp - 15 * 60 * 1000
                  )
                : new Date(ELECTION_DAY_TRANSMISSION_START),
            y: 0,
          });
          timestamps.push(
            getFormattedDateTime(
              cumulativeVotesChartData[0].timestamp <
                ELECTION_DAY_TRANSMISSION_START
                ? new Date(
                    cumulativeVotesChartData[0].timestamp - 15 * 60 * 1000
                  )
                : new Date(ELECTION_DAY_TRANSMISSION_START)
            )
          );
          transmissionPercentage.push("0");

          cumulativeVotesChartData.forEach((cumulativeData) => {
            const candidateData = cumulativeData.data.find(
              (item) =>
                item.candidateSlug.toLowerCase() ===
                candidate.wp_slug.toLowerCase()
            );
            if (candidateData) {
              data.push({
                x: new Date(cumulativeData.timestamp),
                y: candidateData.voteCount,
              });
              timestamps.push(getFormattedDateTime(cumulativeData.timestamp));
              transmissionPercentage.push(
                getTransmissionPercentage(
                  cumulativeData.precinctsTransmitted,
                  TOTAL_PRECINCTS_PH
                )
              );
            }
          });

          datasets.push({
            label: `${candidate.last_name}, ${
              candidate.alternate_name
                ? candidate.alternate_name
                : candidate.first_name
            }`,
            data,
            timestamps,
            rawName: candidate.raw_name,
            thumbnail: candidate.thumbnail,
            transmissionPercentage,
            borderColor: candidate.color,
            borderWidth: 1,
            backgroundColor: candidate.color,
            pointStyle: candidate.legend,
            pointBorderColor: candidate.color,
            pointBorderWidth: 2,
            pointRadius: 2,
            pointHoverRadius: 10,
          });
        }
      });
    } else {
      datasets.push({
        label: "",
        data: null,
      });
    }

    return {
      labels,
      datasets,
    };
  };

  return (
    <div className={styles.mapResultsContainer} id="map-results-container">
      <div className={styles.content}>
        <div
          data-id="map-ranking-container"
          className={`${styles.rankingContainer} ${
            showCandidateList ? styles.open : ""
          }`}
        >
          <div className={styles.chartContainer}>
            <MapCumulativeChart
              dataChart={cumulativeVotesData()}
              title="Cumulative Votes per Candidate"
              description="Philippines + OAV votes"
            />
          </div>
          <MapChartLegend candidatesList={presidential.results} />
        </div>
      </div>
    </div>
  );
};

export default EmbedCumulativeMapPresident;

// vPresidential.results
